<template>
  <div
    ref="tmTabs"
    class="tm-tabs"
    :class="[
      {
        'tm-tabs--no-border': noBorder,
        'tm-tabs--tabs-grow': tabsGrow,
        'tm-tabs--top-border': topBorder,
        'tm-tabs--centering': centering,
        'tm-tabs--no-responsive': noResponsive,
        'tm-tabs--auto-size': autoSize,
      },
      styleClass,
    ]"
    @keydown="onKeydownNavigate"
  >
    <q-tabs v-bind="qTabsProps">
      <component
        :is="tabWrapperComponent"
        class="tm-tabs__wrapper"
        :dropdown-close-on-click="true"
        :dropdown-position="dropdownPosition"
      >
        <template v-slot:dropdown-activator>
          <tm-button
            class="neutral--text pb-2"
            text-link
            flat
            flat-clicked
          >
            <span class="tm-tabs__more">
              {{ $t('common.more') }}
            </span>
            <tm-icon
              name="tmi-arrow-drop-down-sharp"
              class="mr-0 ml-1 distinct--text"
            />
          </tm-button>
        </template>
        <div
          v-for="tab in preparedTabs"
          :key="tab.name"
          class="tm-tabs__item"
          v-bind="getDataTestId('tab', tab.name)"
        >
          <component
            :is="tabComponent"
            :name="tab.name"
            :to="tab.to"
            class="tm-tabs__tab"
            :class="{ 'tm-tabs__tab--flat': flat }"
            @click.capture="handleTabClick(tab.to, $event)"
          >
            <div class="d-flex align-center">
              <slot
                name="label"
                v-bind="{ tab }"
              >
                {{ tab.label ? tab.label : tab.name }}
              </slot>
              <span
                v-if="isShowCount(tab)"
                class="tm-tabs__counter"
                :class="{
                  'tm-tabs__counter--error': tab.error,
                  [tab.counterClass || '']: true,
                }"
                v-text="tab.counter"
              />
              <tm-icon
                v-if="tab.rightIcon"
                class="ml-1"
                v-bind="tab.rightIcon"
              />
            </div>
          </component>
        </div>
      </component>
    </q-tabs>
  </div>
</template>

<script setup lang="ts" generic="T extends string">
import type { RouteLocationRaw } from 'vue-router'
import { QRouteTab, QTab } from 'quasar'
import { computed, useTemplateRef } from '@/composition/vue/compositionApi'
import { DefaultInputEvent } from '@/services/forms/types'
import type { Tab, TabStyle } from '@/components/shared/types'
import TmButton from '@/components/shared/TmButton.vue'
import TmIcon from '@/components/shared/TmIcon.vue'
import { getRouterPermissionsService, getRouterService } from '@/core/container/ioc'
import { useTabsHooks } from '@/composition/tabs'
import { useDataTestId } from '@/composition/testing/dataTestId'
import TmAutoSizePanel from '@/components/shared/autoSize/TmAutoSizePanel.vue'
import type { ControlPosition } from '@/composition/tooltip/renderTooltip'

const props = withDefaults(
  defineProps<{
    tabs: Tab<T>[]
    tabStyle?: TabStyle
    modelValue?: string
    dropdownPosition?: ControlPosition
    wrapperHorizontalPadding?: string
    useRouter?: boolean
    noBorder?: boolean
    noResponsive?: boolean
    tabsGrow?: boolean
    showCounterZero?: boolean
    topBorder?: boolean
    autoSize?: boolean
    centering?: boolean
  }>(),
  {
    modelValue: '',
    tabStyle: 'default',
  },
)

const emit = defineEmits({
  [DefaultInputEvent]: (_value: T) => true,
})

const routerPermissionsService = getRouterPermissionsService()
const routerService = getRouterService()
const { onBeforeTabLeave } = useTabsHooks()

// @todo maybe better move in another component
const preparedTabs = computed<Tab[]>(() => {
  if (!props.useRouter) {
    return props.tabs
  }
  return props.tabs.filter(({ to }) => {
    if (!to) {
      return true
    }
    const routeName = routerService.getRawRouteName(to)
    if (!routeName) {
      return true
    }
    return routerPermissionsService.isAllowAccessToRoute(routeName)
  })
})

const styleClass = computed(() => {
  switch (props.tabStyle) {
    case 'pills':
      return 'tm-tabs--pills'
    case 'flat':
      return 'tm-tabs--flat'
    default:
      return 'tm-tabs--default'
  }
})

const flat = computed(() => props.tabStyle === 'flat')

const tabComponent = computed(() => (props.useRouter ? QRouteTab : QTab))
const tabWrapperComponent = computed(() => (props.autoSize ? TmAutoSizePanel : 'div'))

const onUpdate = async (name: T) => {
  if (onBeforeTabLeave.value && onBeforeTabLeave.value() === false) {
    return
  }
  emit(DefaultInputEvent, name)
}

// Quasar check callback for 'onUpdate:model-value' and change his behaviour (QTabs.js quasar version 2.5.4)
const qTabsProps = computed(() => {
  const obj: any = {
    align: 'left',
    modelValue: props.modelValue,
    'onUpdate:model-value': onUpdate,
  }
  return obj
})

const isShowCount = (tab: Tab): boolean => {
  if (tab.counter === undefined) {
    return false
  }
  if (!Number.isFinite(tab.counter)) {
    return !!tab.counter
  }
  if (props.showCounterZero) {
    return true
  }
  const count = typeof tab.counter === 'number' ? tab.counter : parseInt(tab.counter, 10)
  return !!count
}

const handleTabClick = (to: RouteLocationRaw | undefined, event: PointerEvent) => {
  /**
   * If a tab is a router-link we shouldn't navigate to the same route again,
   * because this will remove query params set by resolvers
   */
  if (to && routerService.isCurrentRoute(to)) {
    event.preventDefault()
  }
}

const rTmTabs = useTemplateRef<HTMLDivElement>('tmTabs')

// Arrows navigation in tabs (ARIA standard).
// Div wrappers hides tabs from Quasar's built-in arrow navigation.
const onKeydownNavigate = (e: KeyboardEvent) => {
  const navKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
  if (!navKeys.includes(e.key) || rTmTabs.value === null) {
    return
  }
  const tabEls = Array.from(rTmTabs.value.querySelectorAll<HTMLElement>('[role="tab"]:not([aria-disabled="true"])'))
  const currentIndex = tabEls.findIndex((el) => el === document.activeElement)
  if (currentIndex === -1) {
    return
  }
  let nextIndex = currentIndex
  switch (e.key) {
    case 'ArrowLeft':
      nextIndex = (currentIndex - 1 + tabEls.length) % tabEls.length
      break
    case 'ArrowRight':
      nextIndex = (currentIndex + 1) % tabEls.length
      break
    case 'Home':
      nextIndex = 0
      break
    case 'End':
      nextIndex = tabEls.length - 1
      break
    default:
      return
  }
  if (nextIndex < 0 || nextIndex >= tabEls.length) {
    return
  }
  e.preventDefault()
  tabEls[nextIndex].focus()
}

const { getDataTestId } = useDataTestId()()
</script>

<style lang="scss" scoped>
.tm-tabs {
  border-bottom: 1px solid $border-color;
  max-width: 100%;
  &__wrapper {
    display: flex;
    flex-grow: 1;
    padding: 0 v-bind(wrapperHorizontalPadding);
  }
  &--no-border {
    border: none;
  }

  &__counter {
    margin-left: 6px;
    font-weight: $bold-font;
    font-size: $sm-font;
    color: $gray5;

    &--error {
      font-weight: $bold-font;
      color: $red;
    }
  }

  &--tabs-grow {
    .tm-tabs__item {
      flex-grow: 1;
    }
  }

  &:not(&--tabs-grow) {
    .tm-tabs__item {
      &:not(:last-child) {
        margin-right: 40px;
      }
    }
    &:deep(.q-tab) {
      flex: 0;
    }
  }

  &--auto-size:not(&--tabs-grow) {
    .tm-tabs__item:not(:last-child) {
      margin-right: 0;
      padding-right: 40px;
    }
  }

  &--auto-size .tm-tabs__wrapper {
    --tm-auto-size-panel-gap: 0;
    align-items: baseline;
  }

  &--auto-size#{&}--pills .tm-tabs__wrapper {
    flex-wrap: nowrap;
    margin: 0;
  }

  &--auto-size#{&}--pills:not(#{&}--tabs-grow) .tm-tabs__item:not(:last-child) {
    margin-right: 0 !important;
    padding-right: 8px;
  }

  &:deep(.q-tabs__arrow) {
    display: none;
  }

  &:deep(.q-tab) {
    padding: 0 0 16px;
    min-height: 0;

    &:hover {
      .q-tab__content {
        color: $emphasize;
      }
      .q-tab__indicator {
        opacity: 1;
      }
    }

    .q-focus-helper {
      display: none;
    }

    &:focus-visible {
      outline: 2px solid $blue400;
      outline-offset: -2px;
    }

    .q-ripple {
      display: none;
    }

    &.q-tab--active {
      .q-tab__content {
        color: $emphasize;
      }
      .q-tab__indicator {
        z-index: 1;
        opacity: 1;
        background-color: $blue;
      }
    }

    &.q-tab--inactive {
      opacity: 1;
    }

    .q-tab__indicator {
      height: 3px;
      transition: all $transition-300ms-ease-in-out;
      background: $input;
      border-top-right-radius: $border-radius;
      border-top-left-radius: $border-radius;
    }

    .q-tab__content {
      padding: 0;
      min-width: 0;
      min-height: 0;
      flex-wrap: nowrap;
      flex-direction: row;
      color: $neutral;
      font-size: $font-size-root;
      line-height: 20px;
      text-transform: none;
    }
  }

  &:deep(.q-tabs__content) {
    margin: -4px;
    padding: 4px;
  }

  &--top-border {
    border-top: 1px solid $gray1;
    border-bottom: none;

    &:deep(.q-tab) {
      padding: 16px 0;

      .q-tab__indicator {
        bottom: auto;
        top: 0;
      }
    }
  }
  &--pills {
    .tm-tabs__wrapper {
      flex-wrap: wrap;
      @include media-lg-max {
        margin: 0 -8px -8px 0;
      }
    }
    &:not(.tm-tabs-tabs-grow) {
      .tm-tabs__item {
        &:not(:last-child) {
          margin-right: 8px;
        }
        @include media-lg-max {
          margin: 0 8px 8px 0 !important;
        }
      }
    }
    &:deep(.q-tab) {
      background: $white;
      border-radius: 20px;
      padding: 0 12px;
      border: 1px solid $border-color;

      .q-tab__indicator {
        display: none;
      }

      .q-tab__content {
        color: $neutral;
        line-height: 24px;
        font-size: $font-size-root;
        font-weight: $semi-bold-font;
      }

      .tm-tabs__counter {
        margin-left: 4px;
        font-size: $font-size-root;
        font-weight: $semi-bold-font;
        color: $light;
      }

      &.q-tab--active {
        background: $neutral;

        .q-tab__content {
          color: $white;
        }

        .tm-tabs__counter {
          color: $subtle;
        }
      }
    }
  }
  &--centering:deep(.q-tab) {
    padding: 16px 0;
  }

  &--flat {
    &:deep(.q-tab .q-tab__content) {
      font-size: 13px;
      line-height: 20px;
      font-weight: 500;
    }

    .tm-tabs__counter {
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
    }

    .tm-tabs__more {
      font-size: 13px;
      line-height: 20px;
      font-weight: 500;
    }
  }

  &__more {
    font-size: $font-size-root;
    font-weight: $semi-bold-font;
    line-height: 18px;
  }

  &--default:not(.tm-tabs--no-responsive) {
    @include media-sm-x-max {
      border: none;
      .tm-tabs__wrapper {
        overflow: hidden;
        flex-direction: column;
      }

      .tm-tabs__item {
        &:not(:last-child) {
          margin: 0 0 4px !important;
        }
        &:deep(.q-tab) {
          padding: 0;
          border-radius: $border-radius;
          background: $shade;
          height: 40px;
          border: 1px solid transparent;
          .q-tab__content {
            color: $neutral;
          }
          &.q-tab--active {
            border-color: $primary;
            .q-tab__content {
              color: $primary;
            }
          }
          .q-tab__indicator {
            display: none;
          }
        }
      }
    }
  }
}
.tm-dropdown__menu-wrapper {
  .tm-tabs__tab {
    justify-content: flex-start;
    min-width: 150px;
    min-height: unset;
    font-weight: $medium-font;
    line-height: 36px;

    &--flat {
      font-size: 13px;
      font-weight: 500;

      .tm-tabs__counter {
        font-size: 12px;
        font-weight: 500;
      }

      &:deep(.q-tab__content) {
        font-size: 13px;
        font-weight: 500;
      }
    }
    text-align: left;
    text-transform: initial;
    border-radius: 0;

    &.q-router-link--exact-active {
      background: $weak;
    }

    &:deep(.q-tab__content) {
      padding: 0;
    }

    &:deep(.q-tab__indicator) {
      display: none;
    }
  }
}
</style>
